"use client";

import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import {
  startTransition,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

import { ReCaptchaProvider } from "@/components/providers/ReCaptchaProvider";
import { ContactFormPanel } from "@/components/shared/ContactFormPanel";

type ContactPopupProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  formKey: number;
};

export function ContactPopup({ open, onOpenChange, formKey }: ContactPopupProps) {
  const [mounted, setMounted] = useState(false);

  const handleCloseComplete = useCallback(() => {
    setMounted(false);
  }, []);

  useLayoutEffect(() => {
    if (open) {
      startTransition(() => setMounted(true));
    }
  }, [open]);

  if (!mounted || typeof document === "undefined") return null;

  return createPortal(
    <ReCaptchaProvider>
      <ContactPopupInner
        open={open}
        onOpenChange={onOpenChange}
        onCloseComplete={handleCloseComplete}
        formKey={formKey}
      />
    </ReCaptchaProvider>,
    document.body,
  );
}

type ContactPopupInnerProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCloseComplete: () => void;
  formKey: number;
};

function ContactPopupInner({ open, onOpenChange, onCloseComplete, formKey }: ContactPopupInnerProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLElement>(null);

  useGSAP(
    () => {
      const backdrop = backdropRef.current;
      const panel = panelRef.current;
      if (!backdrop || !panel) return;

      gsap.killTweensOf([backdrop, panel]);

      if (open) {
        gsap.set(backdrop, { autoAlpha: 0 });
        gsap.set(panel, { yPercent: 100 });
        gsap
          .timeline()
          .to(backdrop, { autoAlpha: 1, duration: 0.35, ease: "power2.out" })
          .to(panel, { yPercent: 0, duration: 0.55, ease: "power3.out" }, 0);
      } else {
        gsap
          .timeline({
            onComplete: () => {
              startTransition(() => {
                onCloseComplete();
              });
            },
          })
          .to(panel, { yPercent: 100, duration: 0.45, ease: "power3.in" })
          .to(backdrop, { autoAlpha: 0, duration: 0.28, ease: "power2.in" }, "<0.15");
      }

      return () => {
        gsap.killTweensOf([backdrop, panel]);
      };
    },
    { dependencies: [open, onCloseComplete] },
  );

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onOpenChange(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onOpenChange]);

  return (
    <div ref={rootRef} className="fixed inset-0 z-230" role="presentation">
      <button
        ref={backdropRef}
        type="button"
        aria-label="Close contact form"
        className="absolute inset-0 bg-black/50 opacity-0"
        onClick={() => onOpenChange(false)}
      />
      <section
        ref={panelRef}
        className="absolute inset-x-0 bottom-0 w-full max-w-none max-h-[calc(100dvh-0.5rem)] overflow-y-auto overscroll-contain rounded-t-2xl bg-[#f2f2f2] pb-[max(1rem,env(safe-area-inset-bottom))] pt-4 text-[#22242a] shadow-[0_-12px_40px_-20px_rgba(0,0,0,0.25)] will-change-transform md:max-h-none md:overflow-visible md:pb-12 md:pt-10"
        role="dialog"
        aria-modal="true"
        aria-label="Contact form"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="site-content">
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-[#25272d] text-lg leading-none text-white"
              aria-label="Close"
            >
              ×
            </button>
          </div>

          <div className="mt-1 grid grid-cols-1 gap-5 md:mt-2 md:gap-10 lg:grid-cols-12 lg:gap-12">
            <div className="lg:col-span-5">
              <h2 className="text-3xl font-medium leading-[0.9] tracking-tight sm:text-5xl md:text-6xl">
                Want to
                <br />
                talk to us?
                <br />
                <span className="text-[#ff5c00]">Get in touch.</span>
              </h2>

              <div className="mt-7 w-full max-w-none space-y-5 text-base leading-[1.4] text-[#22242a]/85 md:mt-10 md:max-w-88 md:space-y-6 md:leading-[1.4]">
                <p>
                  Filling out contact forms isn&apos;t usually high on anyone&apos;s list of exciting activities, we did
                  briefly consider adding a quiz, a plot twist, maybe even a prize at the end… but alas, it&apos;s still
                  just a form.
                </p>
                <p>
                  So, feel free to fill in the contact form opposite or if you&apos;d rather skip straight to a real
                  conversation, give Christopher a call.
                </p>
              </div>

              <div className="mt-4 space-y-3 text-base leading-[1.4] text-[#22242a]/85 md:mt-5 md:space-y-4">
                <a
                  href="tel:+6421471358"
                  className="inline-block text-lg font-bold text-[#ff5c00] underline-offset-2 hover:underline md:text-xl"
                >
                  021 471 358
                </a>
                <address className="not-italic">76 Collingwood Street, Nelson 7010</address>
              </div>
            </div>

            <div className="lg:col-span-7">
              <p className="max-w-[580px] text-base font-medium leading-tight tracking-tight sm:text-2xl">
                Please fill in the form below.
                <br />
                We aim to reply within 1 business day.
              </p>

              <ContactFormPanel key={formKey} />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
